// src/components/multi-select.js

import React, { useEffect, useState } from "react";
import { cva } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  WandSparkles,
  X,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Separator } from "../../Components/ui/separator";
import { Button } from "../../Components/ui/button";
import { Badge } from "../../Components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../Components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../../Components/ui/command";

import oneBar from "../../Assets/Images/oneBar.svg";
import twoBar from "../../Assets/Images/twoBars.svg";
import threeBar from "../../Assets/Images/threeBars.svg";
import CurrencyFlag from "react-currency-flags";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150  duration-300",
  {
    variants: {
      variant: {
        default: "border-foreground/10 text-foreground bg-card ",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground ",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground ",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

/**
 * Props for MultiSelect component
 */
const MultiSelect = React.forwardRef(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      animation = 0,
      type,
      maxCount = 2,
      modalPopover = false,
      asChild = false,
      className,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = useState(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
      if (defaultValue?.length > 0) {
        setSelectedValues(defaultValue);
      }
    }, []);

    const handleInputKeyDown = (event) => {
      // if (event.key === "Enter") {
      //   setIsPopoverOpen(true);
      // } else if (event.key === "Backspace" && !event.currentTarget.value) {
      //   const newSelectedValues = [...selectedValues];
      //   newSelectedValues.pop();
      //   setSelectedValues(newSelectedValues);
      //   onValueChange(newSelectedValues);
      // }
    };

    const toggleOption = (option) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      // onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const toggleAll = () => {
      if (selectedValues?.length === options?.length) {
        handleClear();
      } else {
        const allValues = options?.map((option) => option.value);
        setSelectedValues(allValues);
        // onValueChange(allValues);
      }
    };

    useEffect(() => {
      if (!isPopoverOpen) {
        const hasChanged =
          selectedValues.length !== defaultValue.length ||
          selectedValues.some((val) => !defaultValue.includes(val));

        if (hasChanged) {
          onValueChange(selectedValues);
        }
      }
    }, [isPopoverOpen]);

    return (
      <Popover
        asChild
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full p-1 rounded-lg  border-background  min-h-9 h-auto items-center justify-between bg-[#005EA247] ",
              className
            )}
          >
            {selectedValues?.length > 0 ? (
              <div className="flex justify-between items-center w-full ">
                <div className="flex flex-wrap items-center text-white font-normal">
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = options?.find((o) => o.value === value);
                    return <p className="pl-2 text-base">{placeholder}</p>;
                  })}
                  {selectedValues?.length > 0 && (
                    <p className="bg-background text-xs rounded-full ml-4 max-w-5 max-h-5 min-w-5 min-h-5 flex items-center justify-center text-whie">
                      {selectedValues?.length}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <ChevronDown className="h-4 mx-2 cursor-pointer text-white" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-base text-white  mx-3">
                  {placeholder}
                </span>

                <ChevronDown className="h-4 cursor-pointer text-white mx-2 " />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 z-[997] "
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command className="bg-background  p-5 w-[20em]">
            <div className="flex justify-between w-full items-center">
              <div className="text-white text-lg font-G_semiBold ">
                Select {type}
              </div>
              <div
                className="text-lightBlue  font-G_semiBold cursor-pointer hover:opacity-80 "
                onClick={() => setIsPopoverOpen(false)}
              >
                <X size={25} />
              </div>
            </div>
            <Separator className="bg-borderChallengeOpacity w-full h-[1px] mt-5 mb-3 " />
            <CommandInput
              placeholder="Search..."
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty className="text-white p-4 text-center">
                No results found.
              </CommandEmpty>
              <CommandGroup>
                {options?.map((option) => {
                  const isSelected = selectedValues?.includes(option.value);
                  return (
                    <>
                      <Separator className="bg-borderChallengeOpacity w-full h-[1px]  my-2 " />

                      <CommandItem
                        key={option.value}
                        onSelect={() => toggleOption(option.value)}
                        className="cursor-pointer"
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-background  opacity-[20] text-white"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <CheckIcon className="h-4 w-4" />
                        </div>
                        {option.icon && (
                          <option.icon className="mr-2 h-4 w-4 text-white" />
                        )}

                        {type == "Currency" ? (
                          <div className="flex flex-row items-center gap-2 w-fit p-1 font-G_regular">
                            <img
                              src={`${
                                option.value === "Low"
                                  ? oneBar
                                  : option.value === "Medium"
                                  ? twoBar
                                  : threeBar
                              }`}
                              alt="charts"
                              className="w-5 h-5"
                            />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full overflow-hidden">
                            <CurrencyFlag
                              currency={option.label}
                              width={10}
                              height={10}
                              className="w-full h-full object-contain bg-center"
                            />
                          </div>
                        )}

                        <span className="text-white">{option.label}</span>
                      </CommandItem>
                    </>
                  );
                })}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <div className="flex items-center justify-between">
                  {selectedValues?.length > 0 && (
                    <>
                      <CommandItem
                        onSelect={handleClear}
                        className="flex-1 justify-center cursor-pointer text-[#3797ED] hover:opacity-80"
                      >
                        Clear
                      </CommandItem>
                      <Separator
                        orientation="vertical"
                        className="flex min-h-6 h-full"
                      />
                    </>
                  )}
                  <CommandItem
                    onSelect={() => setIsPopoverOpen(false)}
                    className="flex-1 justify-center cursor-pointer max-w-full !text-[#3797ED] hover:opacity-80"
                  >
                    Save
                  </CommandItem>
                </div>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues?.length > 0 && (
          <WandSparkles
            className={cn(
              "cursor-pointer my-2 text-foreground bg-background w-3 h-3",
              isAnimating ? "" : "text-white"
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export default MultiSelect;
